import { motion } from "framer-motion";
import { Share2, Target, Bot, Check } from "lucide-react";

const services = [
  {
    icon: Share2,
    title: "Social Media Marketing",
    headline: "We don't just post — we build your brand.",
    description:
      "Done-for-you social media system with strategy, content creation, posting, design, optimization, and monthly reporting.",
    includes: [
      "Strategic content calendar",
      "Reels/carousels/stories",
      "Captions & copywriting",
      "Monthly content shoots",
      "Community management",
      "Graphic design",
      "Hashtag research",
      "Monthly reports + calls",
    ],
  },
  {
    icon: Target,
    title: "Paid Advertising",
    badge: "Bundled",
    headline: "Turn your best content into your top performer.",
    description:
      "Meta ads optimization (Facebook/Instagram) to drive traffic, leads, and revenue.",
    includes: [
      "Campaign strategy & setup",
      "Targeting & testing",
      "Budget optimization",
      "Weekly checks",
      "Ad copy & visuals",
      "Monthly reporting",
    ],
  },
  {
    icon: Bot,
    title: "AI Automation",
    headline: "Automate the small stuff. Focus on growth.",
    description:
      "Automation systems for lead capture, follow-ups, and content repurposing.",
    includes: [
      "DM automation",
      "Lead forms",
      "Email drips",
      "Content workflows",
      "Landing pages",
      "Booking flows",
    ],
  },
];

export default function Services() {
  return (
    <section
      data-testid="services-section"
      id="services"
      className="py-24 md:py-32 bg-dark"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="services-title"
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase text-white"
          >
            Our <span className="text-lime">Services</span>
          </h2>
          <p className="font-body text-white/60 mt-4 max-w-xl mx-auto">
            Everything you need to scale your brand, in one place.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`service-card-${index}`}
              className="relative bg-dark-card border border-white/5 p-6 md:p-8 hover:border-lime/50 transition-all duration-300 group"
            >
              {/* Badge */}
              {service.badge && (
                <span className="absolute top-4 right-4 text-xs font-body font-medium bg-lime/20 text-lime px-3 py-1">
                  {service.badge}
                </span>
              )}

              {/* Icon */}
              <div className="w-12 h-12 bg-dark-secondary border border-white/10 flex items-center justify-center mb-6 group-hover:border-lime/50 transition-colors">
                <service.icon className="w-6 h-6 text-lime" />
              </div>

              {/* Title */}
              <div className="mb-4">
                <h3 className="font-heading font-bold text-xl uppercase text-white">
                  {service.title}
                </h3>
              </div>

              {/* Headline */}
              <p className="font-body font-medium text-white/90 mb-3">
                {service.headline}
              </p>

              {/* Description */}
              <p className="font-body text-sm text-white/50 mb-6">
                {service.description}
              </p>

              {/* Includes */}
              <div className="space-y-2">
                {service.includes.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime flex-shrink-0" />
                    <span className="font-body text-sm text-white/70">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
